import { z } from 'zod';
import { ICS_PROVIDER, icalUrlSchema } from './calendar';
import { idSchema } from './common';
import { EVENT_TYPES } from './inbox';
import { classifyIpAddress, isInternalHostname } from './monitoring';

/**
 * Integracje workspace'u (DATA_MODEL „communication / mail”, ARCHITECTURE §13, docs/INTEGRATIONS.md; Fala 4).
 * Tabela `integrations` trzyma stan połączenia i konfigurację BEZ sekretów (`config`), sekret (refresh token Google,
 * klucz API AI, token systemu faktur) wyłącznie zaszyfrowany kluczem workspace'u (`secret_enc`, envelope:
 * `{ ciphertext, iv, tag, keyVersion }`, AAD = `workspace_id:integrations:id`). Sekret nigdy nie wraca w DTO, logach,
 * eksportach ani kontekście AI.
 *
 * Konektory podłączamy na końcu (decyzja Sebastiana): do tego czasu każda integracja działa na adapterze `fake`
 * ze statusem `simulated` („Symulacja”), a UI i wyniki pokazują to wprost.
 */

/** Rodzaje integracji (kolumna `integrations.kind`). */
export const INTEGRATION_KINDS = [
  'gmail',
  'google_calendar',
  'invoicing',
  'ai',
  'smtp',
  'storage',
  'monitoring',
] as const;
export type IntegrationKind = (typeof INTEGRATION_KINDS)[number];
export const integrationKindSchema = z.enum(INTEGRATION_KINDS);

/**
 * Rodzaje z jedną konfiguracją na workspace (unikalny indeks częściowy `integrations_ws_singleton_key`): przełączenie
 * dostawcy zmienia ten wiersz. Skrzynki i kalendarze mogą być różne (kilka kont Google).
 */
export const INTEGRATION_SINGLETON_KINDS = [
  'invoicing',
  'ai',
  'smtp',
  'storage',
  'monitoring',
] as const satisfies readonly IntegrationKind[];

/**
 * Status połączenia: `disconnected` (Niepodłączona), `connected` (Działa), `error` (Błąd — ostatnia operacja się nie
 * udała, `last_error`), `auth_lost` (Utracono autoryzację — token wygasł albo cofnięty, wymaga ponownego połączenia),
 * `simulated` (Symulacja — adapter fake). CHECK: `simulated` ⇔ dostawca `fake`; `error` / `auth_lost` ⇒ `last_error`.
 */
export const INTEGRATION_STATUSES = ['disconnected', 'connected', 'error', 'auth_lost', 'simulated'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];
export const integrationStatusSchema = z.enum(INTEGRATION_STATUSES);

/** Statusy, w których integracja nie działa i UI pokazuje problem (baner, Dziś, Inbox). */
export const INTEGRATION_PROBLEM_STATUSES = ['error', 'auth_lost'] as const satisfies readonly IntegrationStatus[];

/** Dostawca symulacji (adapter fake): nic nie wychodzi na zewnątrz. */
export const FAKE_PROVIDER = 'fake';

/** Dostawcy per rodzaj (walidacja Zod; w bazie tylko format `^[a-z][a-z0-9_]{1,31}$`, nowy dostawca bez migracji). */
export const INTEGRATION_PROVIDERS = {
  // rodzaj `gmail` = skrzynka pocztowa (nazwa historyczna z Fali 4): Gmail (OAuth Google) albo IMAP/SMTP (np. Zenbox)
  gmail: ['fake', 'gmail', 'imap'],
  // Kalendarz: tajny adres iCal (`ics`, DECISIONS 2026-10-07 „Bez Google Workspace”) albo OAuth Google (alternatywa)
  google_calendar: ['fake', 'google', 'ics'],
  invoicing: ['fake', 'fakturownia'],
  ai: ['fake', 'anthropic', 'openai'],
  smtp: ['fake', 'smtp'],
  storage: ['fake', 'local', 's3'],
  monitoring: ['fake', 'http'],
} as const satisfies Record<IntegrationKind, readonly string[]>;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[IntegrationKind][number];

export function isKnownIntegrationProvider(kind: IntegrationKind, provider: string): boolean {
  return (INTEGRATION_PROVIDERS[kind] as readonly string[]).includes(provider);
}

/** Status wynikający z dostawcy przy zapisie: fake = zawsze „Symulacja”. */
export function initialIntegrationStatus(provider: string): IntegrationStatus {
  return provider === FAKE_PROVIDER ? 'simulated' : 'disconnected';
}

/** Komunikat w UI i wynikach operacji na adapterze fake. */
export const INTEGRATION_SIMULATION_NOTE = 'Symulacja — nic nie zostało wysłane ani pobrane z zewnątrz';

/**
 * Klucze konfiguracji, które wyglądają na sekret (token, hasło, klucz API, refresh token, klucz prywatny). Taki klucz
 * w `config` = VALIDATION_ERROR: sekret idzie tylko do `secret_enc`.
 */
export const SECRET_LIKE_CONFIG_KEY = /(secret|token|passw|api[-_]?key|private[-_]?key|credential|authorization)/i;

function secretLikeKeys(value: unknown, path: string[] = []): string[] {
  if (!value || typeof value !== 'object') return [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_LIKE_CONFIG_KEY.test(k)) out.push([...path, k].join('.'));
    out.push(...secretLikeKeys(v, [...path, k]));
  }
  return out;
}

/**
 * Konfiguracja bez sekretów (`integrations.config`): np. `{ account: 'kontakt@fravity.pl' }` (Gmail),
 * `{ domain: 'fravity' }` (Fakturownia), `{ intervalSec: 120 }` (monitoring). Najwyżej 8 KB JSON.
 */
export const integrationConfigSchema = z.record(z.unknown()).superRefine((cfg, ctx) => {
  for (const key of secretLikeKeys(cfg)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: key.split('.'),
      message: 'Sekrety nie mogą być w konfiguracji integracji',
    });
  }
  if (JSON.stringify(cfg).length > 8192) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Konfiguracja jest za duża' });
  }
});
export type IntegrationConfig = z.infer<typeof integrationConfigSchema>;

/**
 * Szyfrogram sekretu integracji (`integrations.secret_enc`; ten sam kształt co `EncryptedSecret` w @fos/core).
 * Metadane jawne obok szyfrogramu (moduł integrations, Fala 4): `hint` = 4 ostatnie znaki klucza API (UI „…a1B2”),
 * `setAt` = kiedy zapisano (ISO). Odszyfrowanie bierze tylko `ciphertext / iv / tag / keyVersion`; przeszyfrowanie
 * (rotacja DEK) musi zachować `hint` i `setAt`.
 */
export interface IntegrationSecretEnc {
  ciphertext: string;
  iv: string;
  tag: string;
  keyVersion: number;
  hint?: string | null;
  setAt?: string | null;
}

/** Klocek pól integracji (zapis przez usługę integracji; sekret osobno, nigdy w tym obiekcie). */
export const integrationFieldsSchema = z
  .object({
    kind: integrationKindSchema,
    provider: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/, { message: 'Nieprawidłowy dostawca' }),
    config: integrationConfigSchema.default({}),
    /** integracja osobista (Figma 12 „Twoje”); null = firmowa („Firma · zarządza admin”) */
    ownerUserId: idSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (!isKnownIntegrationProvider(v.kind, v.provider)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['provider'], message: 'Nieobsługiwany dostawca' });
    }
  });
export type IntegrationFields = z.infer<typeof integrationFieldsSchema>;

/** DTO integracji: bez sekretu i jego długości; `hasSecret` mówi tylko, czy jest zapisany. */
export interface IntegrationDto {
  id: string;
  kind: IntegrationKind;
  provider: string;
  status: IntegrationStatus;
  simulated: boolean;
  config: IntegrationConfig;
  ownerUserId: string | null;
  hasSecret: boolean;
  connectedBy: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
}

/** Zdarzenia integracji (Inbox właściciela integracji i adminów). */
export const INTEGRATION_EVENT_TYPES = {
  authLost: EVENT_TYPES.integrationAuthLost,
  error: EVENT_TYPES.integrationError,
} as const;

// ── Ustawienia → Integracje (moduł integrations, Fala 4; Figma 12) ─────────────

/**
 * Jak dostawca się uwierzytelnia: `none` (bez konta: symulacja, monitoring HTTP), `api_key` (klucz API wpisywany
 * w Ustawieniach, zapis w `secret_enc`), `oauth_google` (logowanie Google, refresh token w `secret_enc`),
 * `server_env` (konfiguracja serwera w `.env`: SMTP, S3, dysk serwera — w aplikacji tylko podgląd i test),
 * `password` (login i hasło skrzynki IMAP/SMTP wpisywane w Ustawieniach; hasło tylko w `secret_enc`, nigdy w DTO),
 * `secret_url` (tajny adres, np. iCal kalendarza: sam adres jest sekretem — tylko w `secret_enc`, w DTO „ustawiony”).
 */
export const INTEGRATION_AUTH_METHODS = [
  'none',
  'api_key',
  'oauth_google',
  'server_env',
  'password',
  'secret_url',
] as const;
export type IntegrationAuthMethod = (typeof INTEGRATION_AUTH_METHODS)[number];

const PROVIDER_AUTH: Record<string, IntegrationAuthMethod> = {
  fake: 'none',
  gmail: 'oauth_google',
  imap: 'password',
  google: 'oauth_google',
  [ICS_PROVIDER]: 'secret_url',
  fakturownia: 'api_key',
  anthropic: 'api_key',
  openai: 'api_key',
  smtp: 'server_env',
  local: 'server_env',
  s3: 'server_env',
  http: 'none',
};

export function integrationAuthMethod(provider: string): IntegrationAuthMethod {
  return PROVIDER_AUTH[provider] ?? 'api_key';
}

/** Rodzaje ustawiane wyłącznie w konfiguracji serwera (`.env`): w aplikacji podgląd stanu i test połączenia. */
export const SERVER_MANAGED_INTEGRATION_KINDS = ['smtp', 'storage'] as const satisfies readonly IntegrationKind[];
export function isServerManagedKind(kind: IntegrationKind): boolean {
  return (SERVER_MANAGED_INTEGRATION_KINDS as readonly string[]).includes(kind);
}

/**
 * Rodzaje, które wysyłają albo pobierają dane z zewnątrz (poczta klientów, kalendarz, faktury, AI, monitoring).
 * Baner „System działa w trybie symulacji” = żadna z nich nie działa na realnym dostawcy.
 */
export const EXTERNAL_INTEGRATION_KINDS = [
  'gmail',
  'google_calendar',
  'invoicing',
  'ai',
  'monitoring',
] as const satisfies readonly IntegrationKind[];

/** Kolejność na liście (Figma 12: najpierw firma, potem „Twoje”). */
export const INTEGRATION_KIND_ORDER: readonly IntegrationKind[] = [
  'invoicing',
  'ai',
  'monitoring',
  'smtp',
  'storage',
  'gmail',
  'google_calendar',
];

/** Rodzaje domyślnie osobiste (konto Google osoby: „Twoje”). */
export const PERSONAL_INTEGRATION_KINDS = ['gmail', 'google_calendar'] as const satisfies readonly IntegrationKind[];

// ── OAuth Google (szkielet pod etap „K”: bez GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET przycisk jest wyłączony) ──

export const GOOGLE_OAUTH_KINDS = ['gmail', 'google_calendar'] as const satisfies readonly IntegrationKind[];
export type GoogleOAuthKind = (typeof GOOGLE_OAUTH_KINDS)[number];
export const googleOAuthKindSchema = z.enum(GOOGLE_OAUTH_KINDS);

/** Dostawca zapisany po połączeniu konta Google (`INTEGRATION_PROVIDERS`). */
export const GOOGLE_OAUTH_PROVIDER: Record<GoogleOAuthKind, 'gmail' | 'google'> = {
  gmail: 'gmail',
  google_calendar: 'google',
};

/** Zakresy rodzaju (Gmail: odczyt, etykiety i wysyłka po akceptacji; Kalendarz: tylko odczyt). */
export const GOOGLE_OAUTH_SCOPES: Record<GoogleOAuthKind, readonly string[]> = {
  gmail: ['https://www.googleapis.com/auth/gmail.modify'],
  google_calendar: ['https://www.googleapis.com/auth/calendar.readonly'],
};

/** Zakresy tożsamości (adres konta z `id_token`, kontrola „to samo konto co skrzynka”). */
export const GOOGLE_IDENTITY_SCOPES = ['openid', 'email'] as const;

/**
 * Zakresy żądania: tożsamość + zakresy rodzaju. `config.scopes` może je tylko zawęzić do znanych zakresów rodzaju
 * (nigdy poszerzyć o inne usługi Google).
 */
export function googleOAuthScopes(kind: GoogleOAuthKind, config: IntegrationConfig = {}): string[] {
  const allowed = GOOGLE_OAUTH_SCOPES[kind];
  const wanted = Array.isArray(config.scopes)
    ? (config.scopes as unknown[]).filter((s): s is string => typeof s === 'string' && allowed.includes(s))
    : [];
  return [...GOOGLE_IDENTITY_SCOPES, ...(wanted.length > 0 ? wanted : allowed)];
}

/** Ważność `state` (od „Połącz z Google” do powrotu z Google). */
export const GOOGLE_OAUTH_STATE_TTL_SEC = 10 * 60;

/** Ścieżki REST (apps/api/src/routes/oauth.ts) i strona powrotu w SPA. */
export const GOOGLE_OAUTH_START_PATH = '/api/integrations/google/start';
export const GOOGLE_OAUTH_CALLBACK_PATH = '/api/integrations/google/callback';
export const INTEGRATIONS_SETTINGS_PATH = '/ustawienia/integracje';

/** Wynik powrotu z Google: parametr `google` w adresie /ustawienia/integracje (komunikaty w etykietach). */
export const GOOGLE_OAUTH_OUTCOMES = [
  'connected',
  'denied',
  'state',
  'expired',
  'exchange',
  'no_refresh_token',
  'account_mismatch',
  'session',
  'unavailable',
  'step_up',
  'forbidden',
  'error',
] as const;
export type GoogleOAuthOutcome = (typeof GOOGLE_OAUTH_OUTCOMES)[number];

// ── Wejścia usług (router `integrations`) ──────────────────────────────────

/** Klucz API: bez spacji, 8–4096 znaków (format dostawcy sprawdza test połączenia, nie walidacja). */
export const integrationSecretSchema = z
  .string({ required_error: 'Wpisz klucz API' })
  .trim()
  .min(8, 'Klucz jest za krótki')
  .max(4096, 'Klucz jest za długi')
  .refine((v) => !/\s/.test(v), 'Klucz nie może zawierać spacji ani nowych linii');

const fakturowniaDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/, 'Podaj subdomenę konta, np. fravity (z adresu fravity.fakturownia.pl)');

/**
 * Pola konfiguracji, które osoba może zmienić (`integrations.setConfig`). `null` usuwa pole. Konto Google (`account`)
 * i zakresy ustawia logowanie Google, nie formularz.
 */
export const INTEGRATION_EDITABLE_CONFIG = {
  gmail: z.object({}).strict(),
  google_calendar: z
    .object({ calendarId: z.string().trim().min(1, 'Podaj kalendarz').max(200).nullable().optional() })
    .strict(),
  invoicing: z.object({ domain: fakturowniaDomainSchema.nullable().optional() }).strict(),
  ai: z.object({}).strict(),
  smtp: z.object({}).strict(),
  storage: z.object({}).strict(),
  monitoring: z
    .object({
      intervalSec: z
        .number()
        .int()
        .min(60, 'Najczęściej co minutę')
        .max(3600, 'Najrzadziej co godzinę')
        .nullable()
        .optional(),
    })
    .strict(),
} satisfies Record<IntegrationKind, z.ZodTypeAny>;

// ── Poczta systemowa (SMTP z .env: kody logowania, reset hasła, zaproszenia) ─────────────────────────────

/** Lokalny Mailpit (dev i test lokalny konfiguracji produkcyjnej): jedyny serwer poczty systemowej bez TLS. */
export const LOCAL_MAILPIT_SMTP_HOSTS = ['localhost', '127.0.0.1', '::1', 'mailpit'] as const;
export const LOCAL_MAILPIT_SMTP_PORT = 1025;

/**
 * Czy SMTP poczty systemowej to lokalny Mailpit (nazwa lokalna i port 1025). Każdy inny serwer: szyfrowanie
 * wymagane — port 465 TLS od początku, inne porty STARTTLS obowiązkowo (przegląd Fali 8).
 */
export function isLocalMailpitSmtp(host: string, port: number): boolean {
  return (
    (LOCAL_MAILPIT_SMTP_HOSTS as readonly string[]).includes(host.trim().toLowerCase()) &&
    port === LOCAL_MAILPIT_SMTP_PORT
  );
}

// ── Skrzynka IMAP/SMTP (np. Zenbox; DECISIONS 2026-10-07: poczta fravity.pl jest w Zenboxie) ──────────────

/** Dostawca skrzynki IMAP/SMTP (rodzaj `gmail` = skrzynka pocztowa, nazwa z Fali 4). */
export const IMAP_PROVIDER = 'imap';
/** Domyślne porty: IMAP 993 (SSL/TLS), SMTP 465 (SSL/TLS); SMTP 587 = STARTTLS. Połączenie zawsze szyfrowane. */
export const IMAP_DEFAULT_PORT = 993;
export const SMTP_DEFAULT_PORT = 465;
/**
 * Dozwolone porty (przegląd Fali 8, ochrona SSRF): tylko porty poczty z szyfrowaniem — IMAP 993 (SSL/TLS; adapter
 * zawsze łączy się przez TLS), SMTP 465 (SSL/TLS) i 587 (STARTTLS wymagany). Inny port = błąd formularza, więc
 * „Testuj połączenie” nie sprawdza dowolnych usług (np. na publicznym adresie serwera aplikacji).
 */
export const IMAP_ALLOWED_PORTS = [993] as const;
export const SMTP_ALLOWED_PORTS = [465, 587] as const;

/** Skrzynka firmowa (wątki widzą osoby z dostępem do klienta) albo osobista (tylko właściciel; DECISIONS 6.10). */
export const MAILBOX_SCOPES = ['company', 'personal'] as const;
export type MailboxScope = (typeof MAILBOX_SCOPES)[number];

/** Nazwa serwera (bez schematu, portu i ścieżki); co najmniej dwa człony, małe litery, cyfry i myślniki. */
const MAIL_HOST_RE = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Nazwa serwera poczty, z którym wolno się połączyć (przegląd Fali 8, SSRF): nie adres IP (ostatni człon bez litery —
 * `127.0.0.1`, `10.0.0.5`, `127.1`, `169.254.169.254`; domena najwyższego poziomu nigdy nie jest liczbą) i nie nazwa
 * sieci lokalnej (`localhost`, `.local`, `.internal`, `.lan`, `.home.arpa`…). Certyfikat TLS i tak musi pasować do
 * nazwy. Adres z DNS sprawdza dodatkowo adapter przy każdym połączeniu (tylko adresy publiczne).
 */
export function isAllowedMailHost(host: string): boolean {
  const h = host.trim().toLowerCase();
  if (!MAIL_HOST_RE.test(h)) return false;
  if (classifyIpAddress(h) !== null || !/[a-z]/.test(h.split('.').at(-1) ?? '')) return false;
  return !isInternalHostname(h);
}

const mailHostSchema = (what: string) =>
  z
    .string({ required_error: `Podaj serwer ${what}` })
    .trim()
    .toLowerCase()
    .regex(MAIL_HOST_RE, `Podaj nazwę serwera ${what} z panelu hostingu (bez https:// i bez portu)`)
    .refine(
      isAllowedMailHost,
      `Podaj publiczną nazwę serwera ${what} z panelu hostingu (bez adresu IP i bez nazw sieci lokalnej)`,
    );

function allowedPortsText(ports: readonly number[]): string {
  return ports.map((p) => `${p} (${p === 587 ? 'STARTTLS' : 'SSL/TLS'})`).join(' albo ');
}

const mailPortSchema = (what: 'IMAP' | 'SMTP') => {
  const allowed: readonly number[] = what === 'IMAP' ? IMAP_ALLOWED_PORTS : SMTP_ALLOWED_PORTS;
  const message = `Port ${what}: ${allowedPortsText(allowed)}`;
  return z
    .number({ required_error: `Podaj port ${what}`, invalid_type_error: `Podaj port ${what}` })
    .int(`Podaj port ${what}`)
    .refine((p) => allowed.includes(p), message);
};

const mailAccountSchema = z
  .string({ required_error: 'Podaj adres skrzynki' })
  .trim()
  .toLowerCase()
  .max(320, 'Adres jest za długi')
  .email('Podaj adres skrzynki, np. kontakt@twojadomena.pl');

const mailLoginSchema = z
  .string()
  .trim()
  .min(1, 'Podaj login')
  .max(320, 'Login jest za długi')
  .refine((v) => !/\s/.test(v), 'Login nie może zawierać spacji');

/**
 * Komplet ustawień skrzynki IMAP/SMTP w `integrations.config` (bez hasła — hasło wyłącznie w `secret_enc`).
 * Login domyślnie = adres skrzynki (pole tylko, gdy inny).
 */
export const imapSettingsSchema = z.object({
  account: mailAccountSchema,
  imapHost: mailHostSchema('IMAP'),
  imapPort: mailPortSchema('IMAP'),
  smtpHost: mailHostSchema('SMTP'),
  smtpPort: mailPortSchema('SMTP'),
  login: mailLoginSchema.optional(),
});
export type ImapSettings = z.infer<typeof imapSettingsSchema>;

/** Dokąd i jako kto skrzynka loguje się zapisanym hasłem: serwery, porty i login (domyślnie = adres skrzynki). */
function imapConnectionKey(config: Record<string, unknown>): string {
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const host = (v: unknown) => text(v).toLowerCase();
  const port = (v: unknown) => (typeof v === 'number' ? String(v) : text(v));
  const account = host(config.account);
  return JSON.stringify([
    host(config.imapHost),
    port(config.imapPort),
    host(config.smtpHost),
    port(config.smtpPort),
    text(config.login) || account,
  ]);
}

/**
 * Czy zmiana konfiguracji skrzynki IMAP/SMTP zmienia serwer, port albo login (przegląd Fali 8). Wtedy zapisane hasło
 * NIE może zostać: poszłoby przy teście połączenia i synchronizacji na nowy serwer — osoba z prawem zmiany ustawień
 * (także Manager bez dostępu do sejfu) odczytałaby je na własnym serwerze. Zmiana wymaga wpisania hasła ponownie
 * (`integrations.switchMode` z `password`; `setConfig` odrzuca ją dla IMAP). Ta sama funkcja w formularzu (UI).
 */
export function imapConnectionChanged(before: Record<string, unknown>, after: Record<string, unknown>): boolean {
  return imapConnectionKey(before) !== imapConnectionKey(after);
}

/** Pola konfiguracji dostawcy IMAP z formularza (null usuwa pole, np. login wraca do adresu skrzynki). */
const IMAP_EDITABLE_CONFIG = z
  .object({
    account: mailAccountSchema.nullable().optional(),
    imapHost: mailHostSchema('IMAP').nullable().optional(),
    imapPort: mailPortSchema('IMAP').nullable().optional(),
    smtpHost: mailHostSchema('SMTP').nullable().optional(),
    smtpPort: mailPortSchema('SMTP').nullable().optional(),
    login: mailLoginSchema.nullable().optional(),
  })
  .strict();

/** Pola konfiguracji z formularza: dostawca z własnymi polami (IMAP), inaczej pola rodzaju. */
export function editableIntegrationConfigSchema(kind: IntegrationKind, provider: string | null): z.ZodTypeAny {
  if (kind === 'gmail' && provider === IMAP_PROVIDER) return IMAP_EDITABLE_CONFIG;
  // kalendarz z linku iCal: jedyne ustawienie to adres (sekret) — zmiana przez switchMode z `icalUrl`
  if (kind === 'google_calendar' && provider === ICS_PROVIDER) return z.object({}).strict();
  return INTEGRATION_EDITABLE_CONFIG[kind];
}

/**
 * Hasło skrzynki IMAP/SMTP: bez przycinania (spacje mogą być częścią hasła), bez nowych linii. Zapis wyłącznie
 * zaszyfrowany (`secret_enc`, bez podpowiedzi „4 ostatnie znaki”); nigdy w DTO, audycie ani logu.
 */
export const mailboxPasswordSchema = z
  .string({ required_error: 'Wpisz hasło skrzynki' })
  .min(1, 'Wpisz hasło skrzynki')
  .max(1024, 'Hasło jest za długie')
  .refine((v) => !/[\r\n]/.test(v) && !v.includes('\0'), 'Hasło nie może zawierać nowych linii');

/** Wymagania dostawcy wobec konfiguracji (np. Fakturownia: subdomena konta; IMAP: adres, serwery i porty). */
export function integrationConfigProblem(
  provider: string,
  config: IntegrationConfig,
): { path: string; message: string } | null {
  if (provider === 'fakturownia' && typeof config.domain !== 'string') {
    return { path: 'config.domain', message: 'Podaj subdomenę konta Fakturowni' };
  }
  if (provider === IMAP_PROVIDER) {
    const parsed = imapSettingsSchema.safeParse(config);
    const issue = parsed.success ? null : parsed.error.issues[0];
    if (issue) return { path: ['config', ...issue.path].join('.'), message: issue.message };
  }
  return null;
}

export const integrationRefInput = z.object({ id: idSchema }).strict();
export const listIntegrationsInput = z.object({}).strict().optional();

export const setIntegrationConfigInput = z
  .object({
    id: idSchema,
    version: z.number().int().positive(),
    config: z.record(z.unknown()),
  })
  .strict();

export const setIntegrationSecretInput = z.object({ id: idSchema, secret: integrationSecretSchema }).strict();

/** Test połączenia: zapisana integracja (`id`) albo rodzaj z konfiguracji serwera bez wiersza (`kind`: SMTP, pliki). */
export const testIntegrationInput = z
  .object({ id: idSchema.optional(), kind: integrationKindSchema.optional() })
  .strict()
  .superRefine((v, ctx) => {
    if (Boolean(v.id) === Boolean(v.kind)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['id'], message: 'Podaj integrację albo rodzaj' });
    }
  });

export const INTEGRATION_MODES = ['simulated', 'real'] as const;
export type IntegrationMode = (typeof INTEGRATION_MODES)[number];

/**
 * Przełączenie symulacja ↔ realny dostawca. `id` = istniejąca integracja, `kind` = nowa (bez wiersza). Realny
 * dostawca z kluczem API wymaga klucza w tym samym wywołaniu (fake nie ma sekretu: CHECK), logowanie Google idzie
 * przez „Połącz z Google”. Przejście na symulację usuwa zapisany sekret.
 */
export const switchIntegrationModeInput = z
  .object({
    id: idSchema.optional(),
    kind: integrationKindSchema.optional(),
    version: z.number().int().positive().optional(),
    mode: z.enum(INTEGRATION_MODES),
    provider: z
      .string()
      .regex(/^[a-z][a-z0-9_]{1,31}$/, { message: 'Nieprawidłowy dostawca' })
      .optional(),
    secret: integrationSecretSchema.optional(),
    /**
     * hasło skrzynki IMAP/SMTP (dostawca `imap`); brak przy istniejącym = zostaje zapisane, ale tylko gdy serwery,
     * porty i login się nie zmieniają (`imapConnectionChanged`; inaczej VALIDATION_ERROR na `password`)
     */
    password: mailboxPasswordSchema.optional(),
    /** rodzaj skrzynki (tylko skrzynka pocztowa): firmowa albo osobista */
    mailboxScope: z.enum(MAILBOX_SCOPES).optional(),
    /**
     * tajny adres iCal kalendarza (dostawca `ics`): zapis wyłącznie zaszyfrowany, nigdy w odpowiedzi; brak przy
     * istniejącym kalendarzu z linku iCal = zostaje zapisany
     */
    icalUrl: icalUrlSchema.optional(),
    config: z.record(z.unknown()).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (Boolean(v.id) === Boolean(v.kind)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['id'], message: 'Podaj integrację albo rodzaj' });
    }
    if (v.mode === 'real' && (!v.provider || v.provider === FAKE_PROVIDER)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['provider'], message: 'Wybierz dostawcę' });
    }
    if (v.mode === 'simulated' && v.secret !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['secret'], message: 'Symulacja nie ma klucza' });
    }
    if (v.mode === 'simulated' && v.password !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Symulacja nie ma hasła' });
    }
    if (v.mode === 'simulated' && v.icalUrl !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['icalUrl'], message: 'Symulacja nie ma adresu kalendarza' });
    }
  });

export const googleOAuthStartInput = z
  .object({ kind: googleOAuthKindSchema, integrationId: idSchema.optional() })
  .strict();

/** Powrót z Google (parametry adresu callbacku). */
export const googleOAuthCallbackInput = z
  .object({
    state: z.string().min(16).max(256),
    code: z.string().min(1).max(4096).optional(),
    error: z.string().max(200).optional(),
  })
  .strict();

// ── DTO ────────────────────────────────────────────────────────────────────

/** Grupa na liście: firmowa („Firma · zarządza admin”), moja („Twoje”), osobista innej osoby (widzą owner/admin). */
export const INTEGRATION_GROUPS = ['company', 'mine', 'member'] as const;
export type IntegrationGroup = (typeof INTEGRATION_GROUPS)[number];

export interface IntegrationProviderOption {
  provider: string;
  auth: IntegrationAuthMethod;
}

/**
 * Pozycja listy Ustawienia → Integracje. `id = null` = rodzaj bez zapisanej integracji (karta „Niepodłączona”, a dla
 * SMTP i plików — stan z konfiguracji serwera, `managedBy: 'server'`). Sekret: tylko `hasSecret`, 4 ostatnie znaki
 * klucza API (`secretLast4`) i data zapisu; nigdy szyfrogram ani długość.
 */
export interface IntegrationItemDto {
  key: string;
  id: string | null;
  kind: IntegrationKind;
  provider: string | null;
  status: IntegrationStatus;
  simulated: boolean;
  group: IntegrationGroup;
  managedBy: 'app' | 'server';
  authMethod: IntegrationAuthMethod | null;
  ownerUserId: string | null;
  ownerName: string | null;
  config: IntegrationConfig;
  /** krótki opis stanu (konto Google, subdomena, adres serwera) */
  summary: string | null;
  hasSecret: boolean;
  secretLast4: string | null;
  secretSetAt: string | null;
  connectedBy: { id: string; name: string } | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  version: number | null;
  /** realni dostawcy rodzaju (bez symulacji) */
  providers: IntegrationProviderOption[];
  canManage: boolean;
  canDisconnect: boolean;
  canTest: boolean;
}

export interface IntegrationsOverviewDto {
  items: IntegrationItemDto[];
  /** żadna integracja zewnętrzna nie działa na realnym dostawcy: „nic nie wychodzi na zewnątrz” */
  simulationOnly: boolean;
  googleOAuth: { available: boolean; note: string | null };
}

export interface IntegrationTestResult {
  ok: boolean;
  simulated: boolean;
  status: IntegrationStatus;
  message: string;
  checkedAt: string;
}

export interface GoogleOAuthStartResult {
  authorizationUrl: string;
  expiresAt: string;
}

export interface GoogleOAuthCallbackResult {
  outcome: GoogleOAuthOutcome;
  integrationId: string | null;
  account: string | null;
}

/** Komunikaty modułu (PL). */
export const INTEGRATION_MESSAGES = {
  forbidden: 'Integracjami zarządza właściciel, admin albo manager',
  notManageable: 'Tą integracją zarządza jej właściciel',
  serverManaged: 'To ustawienie jest w konfiguracji serwera (.env), nie w aplikacji',
  simulatedNoSecret: 'Symulacja nie ma klucza. Najpierw przełącz na realnego dostawcę',
  noSecretForProvider: 'Ten dostawca nie używa klucza API',
  secretRequired: 'Realny dostawca wymaga klucza API',
  useGoogle: 'Konto Google łączymy przyciskiem „Połącz z Google”',
  googleUnavailable: 'Logowanie Google podłączymy w fazie konektorów',
  simulatedDisconnect: 'Symulacji nie odłączamy — możesz przełączyć ją na realnego dostawcę',
  alreadySimulated: 'Integracja już działa w symulacji',
  exists: 'Ta integracja już istnieje',
  appOnly: 'Połączenie z Google tylko z aplikacji',
  passwordRequired: 'Podaj hasło skrzynki',
  passwordRequiredServerChange:
    'Zmiana serwera, portu albo loginu wymaga ponownego wpisania hasła skrzynki (zapisane hasło nie trafi na nowy serwer)',
  passwordOnlyMailbox: 'Hasło podaje się tylko dla skrzynki IMAP/SMTP',
  useMailboxPassword: 'Skrzynka IMAP/SMTP ma hasło, nie klucz API — zmień je w ustawieniach skrzynki',
  mailboxScopeOnlyMailbox: 'Rodzaj (firmowa / osobista) dotyczy tylko skrzynek pocztowych',
  mailboxScopeForbidden: 'Skrzynkę firmową zmienia na osobistą właściciel albo admin',
  mailboxAddressFixed: 'Adresu podłączonej skrzynki nie zmieniamy. Inny adres dodaj jako nową skrzynkę',
} as const;

export const GOOGLE_OAUTH_UNAVAILABLE_NOTE = 'Do podłączenia w fazie konektorów';
